"use client"

import * as React from "react"
import { LockIcon } from "lucide-react"

import { Skeleton } from "@ibada/ui"

/**
 * A phone page while its data loads, in the shape of the phone pages themselves (the round back button and a
 * title, then a summary block and grouped rows), so a phone never flashes a desktop skeleton or card and the page
 * does not jump when the data lands. `hero` adds the big centred figure detail pages open with.
 */
export function MobilePageSkeleton({
  header = true,
  hero = false,
  groups = [3, 2],
}: {
  /** False inside a page that already has its top bar (a wizard step, a tab). */
  header?: boolean
  hero?: boolean
  /** How many rows each grouped card shows. */
  groups?: readonly number[]
}) {
  return (
    <div aria-busy className='flex flex-col gap-5 pb-2'>
      {header ? (
        <div className='flex items-center gap-2.5'>
          <Skeleton className='size-10 shrink-0 rounded-full' />
          <Skeleton className='h-5 w-40' />
        </div>
      ) : null}
      {hero ? (
        <div className='flex flex-col items-center gap-2.5 py-2'>
          <Skeleton className='h-3.5 w-24' />
          <Skeleton className='h-10 w-48 rounded-xl' />
          <Skeleton className='h-6 w-20 rounded-full' />
        </div>
      ) : header ? (
        <Skeleton className='h-7 w-2/3' />
      ) : null}
      {groups.map((rows, groupIndex) => (
        <div key={groupIndex} className='flex flex-col gap-2'>
          <Skeleton className='mx-4 h-3.5 w-28' />
          <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-card'>
            {Array.from({ length: rows }, (_, rowIndex) => (
              <div key={rowIndex} className='flex min-h-[3.25rem] items-center gap-3 px-4'>
                <Skeleton className='h-3.5 w-1/3' />
                <span className='flex-1' />
                <Skeleton className='h-3.5 w-1/4' />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * A phone page that cannot show its content (no access, nothing found): a calm centred message in the phone
 * style, with an optional action as the green pill, instead of a desktop card.
 */
export function MobilePageMessage({
  title,
  description,
  icon: Icon = LockIcon,
  action,
}: {
  title: string
  description?: React.ReactNode
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
  action?: { label: string; onPress: () => void }
}) {
  return (
    <div className='flex min-h-[60dvh] flex-col items-center justify-center gap-3 px-6 text-center'>
      <span className='flex size-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground'>
        <Icon aria-hidden className='size-6' />
      </span>
      <h2 className='text-xl font-extrabold text-foreground'>{title}</h2>
      {description ? <p className='text-[15px] leading-snug font-medium text-muted-foreground'>{description}</p> : null}
      {action ? (
        <button
          type='button'
          onClick={action.onPress}
          className='mt-2 flex h-[3.25rem] items-center justify-center rounded-full bg-primary px-6 text-base font-extrabold text-primary-foreground active:brightness-95'
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )
}
